import { useMemo } from "react";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import type { ClinicalInsightDateRange } from "src-next/modules/clinical-insights/types/analysisTypes";
import type { LoadPhase } from "src-next/modules/clinical-insights/loadPhase";
import {
  deriveInsightLoadPhase,
  useCachedInsightPayload,
  useReportInsightLoadPhase,
} from "src-next/modules/clinical-insights/hooks/useCachedInsightPayload";
import { useInsightPatientHydration } from "src-next/modules/clinical-insights/hooks/useInsightPatientHydration";

export interface ConductData {
  conduct: string;
  status: string;
  practitioner: string;
  date: string;
  patientId: string;
  patientName?: string;
}

interface ConductEntriesPayload {
  entries: unknown[];
}

interface UseConductAnalysisDataArgs {
  author: string;
  practitionerFilter: string;
  dateRange?: ClinicalInsightDateRange;
  getPractitionerName: (ref: string) => string;
  onLoadPhaseChange?: (phase: LoadPhase) => void;
  fetchEnabled?: boolean;
  isActive?: boolean;
}

function processEntriesToConducts(
  entries: unknown[],
  getPractitionerName: (ref: string) => string
): ConductData[] {
  const conductMap = new Map<
    string,
    {
      conduct: string;
      status: string;
      practitioner: string;
      date: string;
      patientId: string;
      authored: Date;
    }
  >();

  for (const entry of entries) {
    const resource = (entry as { resource?: fhir5.QuestionnaireResponse })?.resource;
    if (!resource || resource.resourceType !== "QuestionnaireResponse") continue;

    const conductsItem = resource.item?.find(
      (item) => item.linkId === "38adde2c-f083-435f-8a1f-fc9cc6fa740a"
    );
    if (!conductsItem?.item) continue;

    const patientId = resource.subject?.reference || "unknown";
    const practitionerName = getPractitionerName(resource.author?.reference || "");
    const authoredDate = resource.authored ? new Date(resource.authored) : new Date(0);
    const date = authoredDate.toLocaleDateString();
    const seenInThisQuestionnaire = new Set<string>();

    for (const conductGroup of conductsItem.item) {
      if (
        !conductGroup.item ||
        conductGroup.linkId !== "a6f78af6-6fd9-49a7-e606-491065403d2d"
      ) {
        continue;
      }

      const conductItem = conductGroup.item.find(
        (item) => item.linkId === "3cccab72-a2ed-46e9-ebce-a1c6532e4bca"
      );
      const conductText = conductItem?.answer?.[0]?.valueString;
      if (!conductText) continue;

      const statusItem = conductGroup.item.find(
        (item) => item.linkId === "2e202c22-d5bc-4243-870e-d342cb9c1b8d"
      );

      let status = "aberta";
      if (statusItem?.answer?.[0]?.valueCoding) {
        const statusCode = statusItem.answer[0].valueCoding.code;
        const statusDisplay = statusItem.answer[0].valueCoding.display;
        if (statusCode === "concluded" || statusDisplay === "Realizada") {
          status = "realizada";
        } else if (statusCode === "partially" || statusDisplay === "Parcialmente") {
          status = "parcial";
        } else if (statusCode === "failed" || statusDisplay === "Não realizada") {
          status = "não realizada";
        }
      }

      const uniqueKey = status === "aberta" ? conductText : `${conductText}-${status}`;
      if (seenInThisQuestionnaire.has(uniqueKey)) continue;
      seenInThisQuestionnaire.add(uniqueKey);

      const key =
        status === "aberta"
          ? `${patientId}-${conductText}`
          : `${patientId}-${conductText}-${status}`;

      const existing = conductMap.get(key);
      if (!existing || authoredDate > existing.authored) {
        conductMap.set(key, {
          conduct: conductText,
          status,
          practitioner: practitionerName,
          date,
          patientId,
          authored: authoredDate,
        });
      }
    }
  }

  const patientLatestAuthored = new Map<string, number>();
  for (const v of conductMap.values()) {
    const t = v.authored.getTime();
    const prev = patientLatestAuthored.get(v.patientId) || 0;
    if (t > prev) patientLatestAuthored.set(v.patientId, t);
  }

  type ConductEntry = {
    conduct: string;
    status: string;
    practitioner: string;
    date: string;
    patientId: string;
    authored: Date;
  };
  const groups = new Map<string, ConductEntry[]>();
  for (const value of conductMap.values()) {
    const key = `${value.patientId}|${value.conduct}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(value);
  }

  const parityConducts: ConductData[] = [];
  for (const [, items] of groups.entries()) {
    if (items.length === 1) {
      parityConducts.push({
        conduct: items[0].conduct,
        status: items[0].status,
        practitioner: items[0].practitioner,
        date: items[0].date,
        patientId: items[0].patientId,
      });
      continue;
    }

    const sorted = items.sort((a, b) => b.authored.getTime() - a.authored.getTime());
    const isPair = items.length % 2 === 0;
    const withStatus = sorted.filter((it) => it.status && it.status !== "aberta");
    const withoutStatus = sorted.filter((it) => !it.status || it.status === "aberta");
    const latestAuthoredTs = patientLatestAuthored.get(sorted[0].patientId);
    const latestBelongsToCurrentQR =
      latestAuthoredTs !== undefined && sorted[0].authored.getTime() === latestAuthoredTs;

    if (latestBelongsToCurrentQR) {
      for (const item of sorted) {
        parityConducts.push({
          conduct: item.conduct,
          status: item.status,
          practitioner: item.practitioner,
          date: item.date,
          patientId: item.patientId,
        });
      }
    } else if (isPair) {
      if (withStatus.length > 0) {
        parityConducts.push({
          conduct: withStatus[0].conduct,
          status: withStatus[0].status,
          practitioner: withStatus[0].practitioner,
          date: withStatus[0].date,
          patientId: withStatus[0].patientId,
        });
      } else {
        parityConducts.push({
          conduct: sorted[0].conduct,
          status: sorted[0].status,
          practitioner: sorted[0].practitioner,
          date: sorted[0].date,
          patientId: sorted[0].patientId,
        });
      }
    } else if (withStatus.length > 0) {
      parityConducts.push({
        conduct: withStatus[0].conduct,
        status: withStatus[0].status,
        practitioner: withStatus[0].practitioner,
        date: withStatus[0].date,
        patientId: withStatus[0].patientId,
      });
      if (withoutStatus.length > 0) {
        parityConducts.push({
          conduct: withoutStatus[0].conduct,
          status: withoutStatus[0].status,
          practitioner: withoutStatus[0].practitioner,
          date: withoutStatus[0].date,
          patientId: withoutStatus[0].patientId,
        });
      }
    } else {
      parityConducts.push({
        conduct: sorted[0].conduct,
        status: sorted[0].status,
        practitioner: sorted[0].practitioner,
        date: sorted[0].date,
        patientId: sorted[0].patientId,
      });
    }
  }

  return parityConducts;
}

export function useConductAnalysisData({
  author,
  practitionerFilter,
  dateRange,
  getPractitionerName,
  onLoadPhaseChange,
  fetchEnabled = true,
  isActive = true,
}: UseConductAnalysisDataArgs) {
  const { data, isLoading, error, refresh } = useCachedInsightPayload<ConductEntriesPayload>({
    analysisId: "condutas",
    organizationId: author,
    dateRange,
    practitionerFilter,
    enabled: fetchEnabled && Boolean(author),
    fetcher: async () => {
      const response = await GetQuestionnaireResponse({
        author,
        practitioner: practitionerFilter,
        hasProtocols: false,
        questionnaire: "Questionnaire/clinicalRegister",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      });
      return { entries: response?.entry || [] };
    },
  });

  const conducts = useMemo(
    () => (data ? processEntriesToConducts(data.entries, getPractitionerName) : []),
    [data, getPractitionerName],
  );

  const patientIds = useMemo(
    () => (data ? conducts.map((conduct) => conduct.patientId) : []),
    [data, conducts],
  );

  const hydration = useInsightPatientHydration(patientIds, Boolean(data));

  const loadPhase = deriveInsightLoadPhase({
    enabled: fetchEnabled && Boolean(author),
    isLoading,
    error,
    hasPayload: data !== undefined,
    patientsLoading: hydration.isLoading,
  });

  useReportInsightLoadPhase(isActive, loadPhase, onLoadPhaseChange);

  return {
    conducts,
    patients: hydration.patientMap,
    loadPhase,
    patientsProgress: hydration.progress,
    hasData: data !== undefined,
    retry: () => {
      void refresh();
    },
  };
}
